import React, { useState } from 'react';

const abbrevSize = (s) => s
  .replace(/^Adulto /, 'A-')
  .replace(/^Adult /, 'A-')
  .replace(/^Niño /, 'N-')
  .replace(/^Boys /, 'N-');

// Compact per-size stock chips: [− A-S 2 +] colored by health.
export const StockMatrix = ({ product, onPatch, isSaving }) => {
  const sizes = product.sizes?.es || [];
  const stockQty = product.stock_qty || {};
  const threshold = product.low_stock_threshold ?? 3;

  const [draftQty, setDraftQty] = useState({});
  const [draftThreshold, setDraftThreshold] = useState(threshold);
  const [fillVal, setFillVal] = useState('');
  const [deltaVal, setDeltaVal] = useState('');

  React.useEffect(() => { setDraftThreshold(threshold); }, [threshold]);

  const getValue = (size) => (draftQty[size] !== undefined ? draftQty[size] : (parseInt(stockQty[size], 10) || 0));

  // Strip legacy composite keys (size|variant) on every commit
  const clean = (q) => Object.fromEntries(Object.entries(q).filter(([k]) => !k.includes('|')));

  const commitAll = (newQty) => {
    const q = clean(newQty);
    onPatch(product.product_id, { stock_qty: q }, { stock_qty: q });
    setDraftQty({});
  };
  const commitCell = () => { if (Object.keys(draftQty).length > 0) commitAll({ ...stockQty, ...draftQty }); };
  const onCellChange = (size, raw) => {
    const v = raw === '' ? 0 : Math.max(0, parseInt(raw, 10) || 0);
    setDraftQty(prev => ({ ...prev, [size]: v }));
  };
  const adjust = (size, delta) => commitAll({ ...stockQty, ...draftQty, [size]: Math.max(0, getValue(size) + delta) });
  const fillAll = () => {
    const v = Math.max(0, parseInt(fillVal, 10) || 0);
    const newQty = { ...stockQty };
    sizes.forEach(s => { newQty[s] = v; });
    commitAll(newQty);
    setFillVal('');
  };
  const bulkDelta = (sign) => {
    const d = Math.max(0, parseInt(deltaVal, 10) || 0);
    if (!d) return;
    const newQty = { ...stockQty };
    sizes.forEach(s => { newQty[s] = Math.max(0, getValue(s) + sign * d); });
    commitAll(newQty);
    setDeltaVal('');
  };
  const commitThreshold = () => {
    if (draftThreshold !== threshold) {
      onPatch(product.product_id, { low_stock_threshold: draftThreshold }, { low_stock_threshold: draftThreshold });
    }
  };

  const chipClasses = (qty) => {
    if (qty <= 0) return 'bg-red-50 border-red-200 text-red-700';
    if (qty <= draftThreshold) return 'bg-amber-50 border-amber-200 text-amber-800';
    return 'bg-emerald-50 border-emerald-200 text-emerald-800';
  };

  if (sizes.length === 0) return null;

  const totalAll = sizes.reduce((s, size) => s + getValue(size), 0);
  const totalCls = totalAll <= 0 ? 'bg-red-100 text-red-700' : totalAll <= draftThreshold * sizes.length ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800';

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`stock-matrix-${product.product_id}`}>
      <span className={`inline-flex items-center justify-center min-w-[34px] px-1.5 py-1 rounded-md text-xs font-black ${totalCls}`}
        title="Total de unidades" data-testid={`stock-total-${product.product_id}`}>
        {totalAll}
      </span>
      <div className="flex items-center gap-1.5 flex-wrap flex-1">
        {sizes.map(size => {
          const qty = getValue(size);
          return (
            <div key={size} className={`inline-flex items-center rounded-md border ${chipClasses(qty)}`}>
              <button
                onClick={() => adjust(size, -1)}
                disabled={isSaving || qty <= 0}
                className="px-1.5 py-0.5 text-sm font-bold hover:bg-white/50 disabled:opacity-25 rounded-l-md leading-none"
                data-testid={`stock-dec-${product.product_id}-${size}`}
                aria-label="Disminuir"
              >−</button>
              <span className="text-[11px] font-bold pl-0.5 whitespace-nowrap">{abbrevSize(size)}</span>
              <input
                type="number" min="0" value={qty}
                onChange={(e) => onCellChange(size, e.target.value)}
                onBlur={commitCell}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                className="w-8 text-center bg-transparent text-[11px] font-black border-0 focus:outline-none focus:ring-1 focus:ring-cyan-500 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid={`stock-cell-${product.product_id}-${size}`}
              />
              <button
                onClick={() => adjust(size, 1)}
                disabled={isSaving}
                className="px-1.5 py-0.5 text-sm font-bold hover:bg-white/50 disabled:opacity-25 rounded-r-md leading-none"
                data-testid={`stock-inc-${product.product_id}-${size}`}
                aria-label="Aumentar"
              >+</button>
            </div>
          );
        })}
      </div>
      <span className="inline-flex items-center gap-2 shrink-0 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          Llenar todo a
          <input type="number" min="0" value={fillVal} onChange={(e) => setFillVal(e.target.value)}
            className="w-10 text-center border border-slate-200 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid={`fill-all-input-${product.product_id}`} />
          <button onClick={fillAll} disabled={isSaving || fillVal === ''}
            className="px-1.5 py-0.5 bg-slate-700 text-white rounded font-semibold disabled:opacity-40"
            data-testid={`fill-all-btn-${product.product_id}`}>Aplicar</button>
        </span>
        <span className="inline-flex items-center gap-1">
          <input type="number" min="0" value={deltaVal} onChange={(e) => setDeltaVal(e.target.value)}
            placeholder="±"
            className="w-10 text-center border border-slate-200 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid={`bulk-delta-input-${product.product_id}`} />
          <button onClick={() => bulkDelta(1)} disabled={isSaving || deltaVal === ''}
            className="px-1.5 py-0.5 bg-emerald-600 text-white rounded font-bold disabled:opacity-40"
            title="Sumar a todas las tallas"
            data-testid={`bulk-add-btn-${product.product_id}`}>+</button>
          <button onClick={() => bulkDelta(-1)} disabled={isSaving || deltaVal === ''}
            className="px-1.5 py-0.5 bg-red-500 text-white rounded font-bold disabled:opacity-40"
            title="Restar a todas las tallas"
            data-testid={`bulk-sub-btn-${product.product_id}`}>−</button>
        </span>
        <span className="inline-flex items-center gap-1 text-slate-400">
          Alerta ≤
          <input
            type="number" min="0" value={draftThreshold}
            onChange={(e) => setDraftThreshold(Math.max(0, parseInt(e.target.value, 10) || 0))}
            onBlur={commitThreshold}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-10 text-center border border-slate-200 rounded px-1 py-0.5 text-[10px] text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            data-testid={`threshold-${product.product_id}`}
          />
        </span>
      </span>
    </div>
  );
};
